import {
  Award,
  CalendarCheck,
  CalendarDays,
  Cookie,
  GraduationCap,
  Heart,
  Home,
  MapPin,
  Megaphone,
  Package,
  Phone,
  ShoppingBag,
  TrendingUp,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { isBuilt } from '@/lib/featureFlags';

export type NavDef = { key: string; label: string; to: string; icon: LucideIcon };

// Home first, then pages in nav priority order.
const NAV_PAGES_RAW: NavDef[] = [
  { key: 'home', label: 'Home', icon: Home, to: '/' },
  { key: 'today', label: 'Today', icon: CalendarCheck, to: '/today' },
  { key: 'inventory', label: 'Inventory', icon: Package, to: '/inventory' },
  { key: 'pulse', label: 'Pulse', icon: ShoppingBag, to: '/pulse' },
  { key: 'sampling', label: 'Sampling', icon: Cookie, to: '/sampling' },
  { key: 'performance', label: 'Performance', icon: TrendingUp, to: '/performance' },
  { key: 'promotions', label: 'Promotions', icon: Megaphone, to: '/promotions' },
  { key: 'loyalty', label: 'Loyalty', icon: Heart, to: '/loyalty' },
  { key: 'recognition', label: 'Recognition', icon: Award, to: '/recognition' },
  // The key stays `training` on purpose: it is what a device has already
  // saved in its bottom-bar preference, so renaming it would quietly drop
  // this tool from the bar for anyone who customized theirs.
  { key: 'training', label: 'Resources', icon: GraduationCap, to: '/resources' },
  { key: 'my-store', label: 'My Store', icon: MapPin, to: '/my-store' },
  { key: 'comms', label: 'Comms', icon: Phone, to: '/comms' },
  { key: 'schedule', label: 'Schedule', icon: CalendarDays, to: '/schedule' },
  // Chat is reached via the floating chat bubble (shell/ChatBubble), not a nav
  // entry, so it is intentionally omitted here. The /chat route still exists.
  { key: 'directory', label: 'Directory', icon: Users, to: '/directory' },
];

// Tools not built yet (comms, schedule, ...) are filtered out of the nav (the
// bottom bar, More, and the Settings menu editor) via the shared NOT_BUILT
// flag. Their routes still exist so they stay reachable by URL; remove a key
// from NOT_BUILT (lib/featureFlags.ts) to restore it to the nav in one line.
export const ALL_NAV_PAGES: NavDef[] = NAV_PAGES_RAW.filter((p) => isBuilt(p.key));

// The bottom-bar tools (beside a fixed Home and a fixed More) a user gets
// before customizing. Matches the previous fixed slice: the first four
// non-home pages.
export const DEFAULT_BAR_KEYS = ALL_NAV_PAGES.filter((p) => p.key !== 'home')
  .slice(0, 4)
  .map((p) => p.key);

// Full nav order given the user's chosen bottom-bar keys: Home first, then the
// chosen tools (in nav-priority order), then the remaining tools. BottomNav
// slices this by the device's tab count; whatever does not fit goes in More.
export function orderedNavPages(barKeys: string[]): NavDef[] {
  const set = new Set(barKeys);
  const home = ALL_NAV_PAGES.filter((p) => p.key === 'home');
  const chosen = ALL_NAV_PAGES.filter((p) => p.key !== 'home' && set.has(p.key));
  const rest = ALL_NAV_PAGES.filter((p) => p.key !== 'home' && !set.has(p.key));
  return [...home, ...chosen, ...rest];
}
